/**
 * loadSlAnim — the Load stage for Second Life / OpenSim binary animation
 * assets (LLKeyframeMotion `.anim`, ADR-008 §2). Format knowledge ends here:
 * downstream conversion consumes the loaded scene graph only, exactly as for
 * GLB/FBX/BVH — no per-format branches past this file.
 *
 * Decoded NATIVELY, never via a BVH round-trip (ADR-191 investigation R1,
 * ratified 2026-08-08): the format is quaternion-native and keyframe-sparse
 * (census p50 4.3 keys/s — SL optimized at upload), and it carries play
 * metadata BVH cannot hold (priority, loop points, ease, hand pose). A BVH
 * intermediary would resample to a dense grid, force a lossy quat→ZXY-Euler
 * →quat trip, and default the metadata this rail exists to recover.
 *
 * The file carries joint NAMES + curves only — no hierarchy, no rest offsets.
 * The vendored canonical Linden skeleton (data/sl-skeleton.ts, 133 bones incl.
 * Bento) supplies structure; a clip animating 19 joints rides a full skeleton
 * whose remaining bones simply hold rest pose. Every rest rotation is identity
 * (asserted at data generation), so a rotation key IS the joint's local
 * rotation — no rest composition.
 *
 * Format specifics owned here (verified against the 2026-08-08 census of
 * 4,940 real files + the Firestorm-ported reference parser):
 * - Little-endian, no alignment padding. Priority is S32 on disk (Firestorm's
 *   own `anim_tool.py` declares S16 and native padding silently corrects it —
 *   do not copy the S16).
 * - Rotation keys: U16 x/y/z → [-1,1], truncated quaternion;
 *   W = √(max(0, 1−x²−y²−z²)), +W hemisphere. NEVER OpenSim's
 *   BinBVHAnimation.cs `1−(x+y+z)` — that formula is wrong (non-unit
 *   quaternions, visible drift).
 * - Position keys: U16 → [-5,+5] m, and they are BIND-RELATIVE offsets around
 *   zero (a standing pelvis authors ≈0 — the census classifies posture by
 *   exactly that signal). The track builder adds the vendored rest offset so
 *   installed tracks carry ABSOLUTE local positions; see the Tracks section.
 *   Times: U16 → [0, duration]. Dequantized values within one quantum of zero
 *   snap to zero (client parity).
 * - Key counts are bounds-checked against the remaining buffer BEFORE reading
 *   (the census found assets truncated mid-file), and exact-EOF is asserted
 *   after the constraint block. Violations refuse loudly (MalformedSlAnimError)
 *   — the caller keeps the source bytes and marks the row (ADR-191 §5).
 * - Legacy SL-BVH joint names ('hip', 'lCollar', 'rToe', …) alias to canonical
 *   bones (skeleton alias table + the census's rToe/lToe strays); the rename is
 *   reported via authoredNameFor (ADR-008 §2j). Unknown names (Poser-era junk:
 *   'cyl_1', 'ball_3') are dropped and SURFACED — never silently swallowed.
 * - Convention is declared by the spec, not sniffed: Z-up, +X facing,
 *   right-handed, meters — provenance 'spec:sl-anim'.
 * Depends on: three, data/sl-skeleton. Depended on by: app/import.
 */
import { AnimationClip, Bone, Object3D, QuaternionKeyframeTrack, VectorKeyframeTrack } from 'three';
import { classifyTake, type TakeClassification } from './load-fbx.js';
import { MalformedSlAnimError, UnsupportedSlAnimVersionError } from './errors.js';
import { SL_SKELETON } from './data/sl-skeleton.js';
import type { ConventionDeclaration } from './import-report.js';
import type { BvhEndSite } from './load-bvh.js';

const U16_MAX = 65535;
const LL_MAX_PELVIS_OFFSET = 5.0;
/** Legacy names the skeleton's own alias table misses (census: 2 unique files). */
const EXTRA_LEGACY_ALIASES: Readonly<Record<string, string>> = { rToe: 'mToeRight', lToe: 'mToeLeft' };

/** The play metadata the .anim header carries and BVH would have destroyed. */
export interface SlAnimMetadata {
  readonly basePriority: number;
  readonly duration: number;
  /** Viewer emote fired alongside the motion ('' = none; census: 32/2,707). */
  readonly emoteName: string;
  readonly loopInPoint: number;
  readonly loopOutPoint: number;
  readonly loop: boolean;
  readonly easeInDuration: number;
  readonly easeOutDuration: number;
  /** llhandmotion.h enum (0=spread, 1=relaxed, …) — census: 90% relaxed. */
  readonly handPose: number;
  /**
   * Per-joint priority overrides (name → positive S32). A stored priority ≤ 0
   * means "inherit basePriority" and is omitted, as are joints at base — only
   * a positive value differing from base is a real override.
   */
  readonly jointPriorities: Readonly<Record<string, number>>;
  /** Constraint records present but not decoded (census: 0 across all corpora). */
  readonly constraintCount: number;
}

/** A joint whose tracks were dropped: name not in the canonical skeleton. */
export interface SlAnimDroppedJoint {
  readonly name: string;
  readonly rotKeys: number;
  readonly posKeys: number;
}

export interface LoadedSlAnim {
  readonly scene: Object3D;
  /** The file's single motion block as one clip (named 'animation', BVH parity). */
  readonly clip: AnimationClip;
  readonly classification: TakeClassification;
  readonly convention: ConventionDeclaration;
  /** Canonical name → authored legacy alias, when an alias attached the tracks. */
  readonly authoredNameFor: (object: Object3D) => string | undefined;
  /** The format carries no generator metadata — honestly unknown. */
  readonly generator: string | null;
  /** No extension mechanism — honestly empty, not unchecked. */
  readonly unhandledExtensions: readonly string[];
  /** Leaf tip offsets from the canonical skeleton, as declared tip hints. */
  readonly endSites: readonly BvhEndSite[];
  readonly metadata: SlAnimMetadata;
  /** Joints named in the file but absent from the canonical skeleton — loud. */
  readonly droppedJoints: readonly SlAnimDroppedJoint[];
}

export interface LoadSlAnimOptions {
  /** Label for error messages (specimen name); defaults to 'sl-anim'. */
  readonly label?: string;
}

/** Cursor over the little-endian byte stream, every read bounds-checked. */
class Reader {
  private off = 0;
  private readonly view: DataView;
  private readonly bytes: Uint8Array;
  constructor(
    buffer: ArrayBuffer,
    private readonly label: string,
  ) {
    this.view = new DataView(buffer);
    this.bytes = new Uint8Array(buffer);
  }
  get offset(): number {
    return this.off;
  }
  get remaining(): number {
    return this.view.byteLength - this.off;
  }
  refuse(detail: string): never {
    throw new MalformedSlAnimError(this.label, detail, this.off);
  }
  private need(n: number, what: string): void {
    if (this.remaining < n) this.refuse(`${what} needs ${n} bytes, ${this.remaining} remain`);
  }
  u16(what: string): number {
    this.need(2, what);
    const v = this.view.getUint16(this.off, true);
    this.off += 2;
    return v;
  }
  u32(what: string): number {
    this.need(4, what);
    const v = this.view.getUint32(this.off, true);
    this.off += 4;
    return v;
  }
  i32(what: string): number {
    this.need(4, what);
    const v = this.view.getInt32(this.off, true);
    this.off += 4;
    return v;
  }
  f32(what: string): number {
    this.need(4, what);
    const v = this.view.getFloat32(this.off, true);
    this.off += 4;
    return v;
  }
  cstr(what: string): string {
    const end = this.bytes.indexOf(0, this.off);
    if (end === -1) this.refuse(`${what}: unterminated string`);
    const s = new TextDecoder().decode(this.bytes.subarray(this.off, end));
    this.off = end + 1;
    return s;
  }
  skip(n: number, what: string): void {
    this.need(n, what);
    this.off += n;
  }
}

/** U16 → [lo, hi], snapping to zero within one quantum (client parity). */
function dequantize(v: number, lo: number, hi: number): number {
  const f = (v / U16_MAX) * (hi - lo) + lo;
  return Math.abs(f) < (hi - lo) / U16_MAX ? 0 : f;
}

interface JointCurves {
  readonly canonical: string;
  readonly authored: string;
  readonly priority: number;
  readonly rotTimes: number[];
  readonly rotValues: number[];
  readonly posTimes: number[];
  /** Bind-relative offsets exactly as decoded; the rest is added at track build. */
  readonly posValues: number[];
}

export function loadSlAnim(source: ArrayBuffer, options: LoadSlAnimOptions = {}): LoadedSlAnim {
  const label = options.label ?? 'sl-anim';
  const r = new Reader(source, label);

  // ---- Header.
  const version = r.u16('version');
  const subVersion = r.u16('sub_version');
  if (version !== 1 || subVersion !== 0) throw new UnsupportedSlAnimVersionError(label, version, subVersion);
  const basePriority = r.i32('base_priority');
  const duration = r.f32('duration');
  if (!Number.isFinite(duration) || duration < 0) r.refuse(`duration ${duration} is not a non-negative number`);
  const emoteName = r.cstr('emote_name');
  const loopInPoint = r.f32('loop_in_point');
  const loopOutPoint = r.f32('loop_out_point');
  const loop = r.i32('loop') !== 0;
  const easeInDuration = r.f32('ease_in_duration');
  const easeOutDuration = r.f32('ease_out_duration');
  const handPose = r.u32('hand_pose');
  const numJoints = r.u32('num_joints');

  // ---- Name resolution: canonical names + skeleton aliases + census strays.
  const canonicalByName = new Map<string, string>();
  for (const bone of SL_SKELETON) {
    canonicalByName.set(bone.name, bone.name);
    for (const alias of bone.aliases ?? []) canonicalByName.set(alias, bone.name);
  }
  for (const [alias, canonical] of Object.entries(EXTRA_LEGACY_ALIASES)) canonicalByName.set(alias, canonical);

  // ---- Joints: bounds-checked curve reads; unknown names dropped LOUDLY.
  //      Key times dequantize to [0, duration]; a zero-duration pose anim
  //      collapses to t=0 keys (classifyTake then reports it static).
  const joints: JointCurves[] = [];
  const droppedJoints: SlAnimDroppedJoint[] = [];
  const jointPriorities: Record<string, number> = {};
  for (let j = 0; j < numJoints; j += 1) {
    const authored = r.cstr(`joint[${j}] name`);
    const priority = r.i32(`joint "${authored}" priority`);
    const numRot = r.i32(`joint "${authored}" num_rot_keys`);
    if (numRot < 0 || numRot * 8 > r.remaining) r.refuse(`joint "${authored}" num_rot_keys ${numRot} overruns buffer`);
    const rotTimes: number[] = [];
    const rotValues: number[] = [];
    for (let k = 0; k < numRot; k += 1) {
      const t = dequantize(r.u16('rot time'), 0, duration);
      const x = dequantize(r.u16('rot x'), -1, 1);
      const y = dequantize(r.u16('rot y'), -1, 1);
      const z = dequantize(r.u16('rot z'), -1, 1);
      const w = Math.sqrt(Math.max(0, 1 - x * x - y * y - z * z));
      // Monotonic guard: a duplicate quantized time would zero a segment.
      if (rotTimes.length > 0 && t <= (rotTimes[rotTimes.length - 1] as number)) continue;
      rotTimes.push(t);
      rotValues.push(x, y, z, w);
    }
    const numPos = r.i32(`joint "${authored}" num_pos_keys`);
    if (numPos < 0 || numPos * 8 > r.remaining) r.refuse(`joint "${authored}" num_pos_keys ${numPos} overruns buffer`);
    const posTimes: number[] = [];
    const posValues: number[] = [];
    for (let k = 0; k < numPos; k += 1) {
      const t = dequantize(r.u16('pos time'), 0, duration);
      const x = dequantize(r.u16('pos x'), -LL_MAX_PELVIS_OFFSET, LL_MAX_PELVIS_OFFSET);
      const y = dequantize(r.u16('pos y'), -LL_MAX_PELVIS_OFFSET, LL_MAX_PELVIS_OFFSET);
      const z = dequantize(r.u16('pos z'), -LL_MAX_PELVIS_OFFSET, LL_MAX_PELVIS_OFFSET);
      if (posTimes.length > 0 && t <= (posTimes[posTimes.length - 1] as number)) continue;
      posTimes.push(t);
      posValues.push(x, y, z);
    }
    const canonical = canonicalByName.get(authored);
    if (canonical === undefined) {
      droppedJoints.push({ name: authored, rotKeys: numRot, posKeys: numPos });
      continue;
    }
    // Format semantics: a stored priority ≤ 0 inherits basePriority — only a
    // positive differing value is a real override (a stored 0 is NOT layer 0).
    if (priority > 0 && priority !== basePriority) jointPriorities[canonical] = priority;
    joints.push({ canonical, authored, priority, rotTimes, rotValues, posTimes, posValues });
  }

  // ---- Constraints: counted, size-validated, skipped (census: none exist).
  const constraintCount = r.i32('num_constraints');
  if (constraintCount < 0 || constraintCount * 86 > r.remaining) {
    r.refuse(`num_constraints ${constraintCount} overruns buffer`);
  }
  r.skip(constraintCount * 86, 'constraint records');
  if (r.remaining !== 0) r.refuse(`${r.remaining} trailing bytes after constraint block — exact EOF is the format`);

  // ---- Scene: the full canonical skeleton, rest pose from vendored offsets.
  const boneByName = new Map<string, Bone>();
  const scene = new Object3D();
  for (const def of SL_SKELETON) {
    const bone = new Bone();
    bone.name = def.name;
    bone.position.set(def.pos[0], def.pos[1], def.pos[2]);
    boneByName.set(def.name, bone);
    if (def.parent === null) scene.add(bone);
    else (boneByName.get(def.parent) as Bone).add(bone);
  }

  // ---- Tracks: local rotation outright (identity rests). Position keys are
  //      BIND-RELATIVE offsets on disk, so each is lifted to an absolute local
  //      position by adding the joint's vendored rest offset — a `.position`
  //      track REPLACES the rest rather than composing with it, and downstream
  //      root-motion retarget subtracts the bind, so installing the raw ≈0
  //      standing offsets would read as a ~1.067 m pelvis sink. mPelvis
  //      position is the root-motion channel; interior-joint position keys
  //      (rare) get the same lift.
  const tracks: (QuaternionKeyframeTrack | VectorKeyframeTrack)[] = [];
  const authoredFor = new Map<Object3D, string>();
  for (const joint of joints) {
    const bone = boneByName.get(joint.canonical) as Bone;
    if (joint.authored !== joint.canonical) authoredFor.set(bone, joint.authored);
    if (joint.rotTimes.length > 0) {
      tracks.push(new QuaternionKeyframeTrack(`${joint.canonical}.quaternion`, joint.rotTimes, joint.rotValues));
    }
    if (joint.posTimes.length > 0) {
      const rest = bone.position;
      const absolute: number[] = [];
      for (let i = 0; i < joint.posValues.length; i += 3) {
        absolute.push(
          (joint.posValues[i] as number) + rest.x,
          (joint.posValues[i + 1] as number) + rest.y,
          (joint.posValues[i + 2] as number) + rest.z,
        );
      }
      tracks.push(new VectorKeyframeTrack(`${joint.canonical}.position`, joint.posTimes, absolute));
    }
  }
  const clip = new AnimationClip('animation', duration, tracks);

  const convention: ConventionDeclaration = Object.freeze({
    metersPerUnit: 1,
    upAxis: 'Z',
    facing: '+X',
    provenance: 'spec:sl-anim',
  });

  const childCounts = new Map<string, number>();
  for (const def of SL_SKELETON) {
    if (def.parent !== null) childCounts.set(def.parent, (childCounts.get(def.parent) ?? 0) + 1);
  }
  const endSites: BvhEndSite[] = SL_SKELETON.filter((d) => !childCounts.has(d.name)).map((d) => ({
    parent: d.name,
    offset: { x: d.end[0], y: d.end[1], z: d.end[2] },
  }));

  return {
    scene,
    clip,
    classification: classifyTake(clip),
    convention,
    authoredNameFor: (object) => authoredFor.get(object),
    generator: null,
    unhandledExtensions: [],
    endSites,
    metadata: {
      basePriority,
      duration,
      emoteName,
      loopInPoint,
      loopOutPoint,
      loop,
      easeInDuration,
      easeOutDuration,
      handPose,
      jointPriorities,
      constraintCount,
    },
    droppedJoints,
  };
}
